import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODateShort } from "@entrilia/shared/utilities/dateUtils";
import type {
  BooleanObjectFilter,
  DateObjectFilter,
  DateObjectFilterOperator,
  EntityFieldType,
  Field,
  GuidFilter,
  MultiSelectObjectFilter,
  MultiSelectObjectFilterOperator,
  NumberObjectFilter,
  NumberObjectFilterOperator,
  ObjectClassDefinition,
  ObjectFilter,
  SelectOption,
  TextObjectFilter,
  TextObjectFilterOperator,
} from "../../api/types/objectTypes";
import type { ObjectFilterFieldGroup } from "./ObjectFilterFieldSelect";

type ConditionObjectDefinition = Pick<ObjectClassDefinition, "objectType" | "fields">;

export const emptyGuid = "00000000-0000-0000-0000-000000000000";

export const getGuidFilterForSelectedValues = (values: string[]): GuidFilter => ({ value: emptyGuid, values });

export const getDefaultFilterForFieldType = (type: EntityFieldType): Partial<ObjectFilter> => {
  switch (type) {
    case "Text":
    case "Email":
    case "Phone":
    case "Url":
      return { textFilter: { operator: "Contains" } };
    case "Number":
    case "Money":
    case "Percent":
      return { numberFilter: { operator: "Equals" } };
    case "Date":
    case "DateTime":
      return { dateFilter: { operator: "Range" } };
    case "Checkbox":
      return { booleanFilter: { value: true } };
    case "Select":
    case "MultiSelect":
    case "UserDefinedOptionsSelect":
    case "UserDefinedOptionsMultiSelect":
      return { multiSelectFilter: { operator: "OneOf", selectedValues: [] } };
    case "Lookup":
    case "MultiLookup":
      return { guidFilter: getGuidFilterForSelectedValues([]) };
    default:
      return {};
  }
};

export const getConditionFieldSelectOptions = (field: Field | undefined): SelectOption[] | undefined =>
  field?.configuration?.$type === "UserDefinedOptionsSelect" ? field.configuration.userDefinedOptions : undefined;

export const getConditionFields = (
  objectType: string,
  objectDefinition: ConditionObjectDefinition,
  relatedObjectDefinitions: Record<string, Pick<ObjectClassDefinition, "fields">>
): Field[] =>
  objectType === objectDefinition.objectType
    ? objectDefinition.fields
    : (relatedObjectDefinitions[objectType]?.fields ?? []);

export const getConditionObjectLabel = (
  objectType: string,
  objectDefinition: Pick<ObjectClassDefinition, "objectType">,
  relatedObjectDefinitions: Record<string, Pick<ObjectClassDefinition, "objectName">>
): string =>
  objectType === objectDefinition.objectType ? "" : (relatedObjectDefinitions[objectType]?.objectName ?? objectType);

export const getChecklistConditionFieldGroups = (
  objectDefinition: ConditionObjectDefinition,
  relatedObjectDefinitions: Record<string, Pick<ObjectClassDefinition, "objectName" | "fields">>,
  supportedFieldTypes: EntityFieldType[]
): ObjectFilterFieldGroup[] => {
  const toGroup = (key: string, label: string, fields: Field[]): ObjectFilterFieldGroup => ({
    key,
    label,
    fields: fields.filter((f) => supportedFieldTypes.includes(f.type)).sort(stringComparerBy((f) => f.name)),
  });

  return [
    toGroup(objectDefinition.objectType, "", objectDefinition.fields),
    ...Object.entries(relatedObjectDefinitions).map(([objectType, def]) =>
      toGroup(objectType, def.objectName, def.fields)
    ),
  ];
};

export const findFieldInGroups = (
  fieldGroups: ObjectFilterFieldGroup[],
  objectType: string,
  fieldId: string
): Field | undefined => fieldGroups.find((group) => group.key === objectType)?.fields.find((f) => f.id === fieldId);

// Boolean

export const getBooleanFilterValueText = (filter: BooleanObjectFilter): string => {
  return filter.value ? "Yes" : "No";
};

// Text

export const textFilterOperatorCaptions: Record<TextObjectFilterOperator, string> = {
  Contains: "contains",
  DoesNotContain: "does not contain",
  EqualsCaseInsensitive: "equals",
  InArray: "is one of",
  Empty: "is empty",
  NotEmpty: "is not empty",
};

export const getTextFilterValuesText = (filter: TextObjectFilter): string => {
  switch (filter.operator) {
    case "Contains":
    case "DoesNotContain":
    case "EqualsCaseInsensitive":
      return filter.value ?? "";
    case "InArray":
      return filter.values?.join(", ") ?? "";
    case "Empty":
    case "NotEmpty":
      return "";
    default:
      return "";
  }
};

// Number

export const numberFilterOperatorCaptions: Record<NumberObjectFilterOperator, string> = {
  Equals: "is equal to",
  NotEquals: "is not equal to",
  Between: "is between",
  GreaterThan: "is greater than",
  GreaterThanOrEquals: "is greater than or equal to",
  LessThan: "is less than",
  LessThanOrEquals: "is less than or equal to",
  Empty: "is empty",
  NotEmpty: "is not empty",
};

export const getNumberFilterValues = (
  filter: NumberObjectFilter
): number | [number | undefined, number | undefined] | undefined => {
  switch (filter.operator) {
    case "Equals":
      return filter.value;
    case "NotEquals":
      return filter.value;
    case "Between":
      return [filter.from, filter.to];
    case "GreaterThan":
      return filter.from;
    case "GreaterThanOrEquals":
      return filter.from;
    case "LessThan":
      return filter.to;
    case "LessThanOrEquals":
      return filter.to;
    case "Empty":
      return undefined;
    case "NotEmpty":
      return undefined;
    default:
      return undefined;
  }
};

export const getNumberFilterValuesText = (filter: NumberObjectFilter): string => {
  const values = getNumberFilterValues(filter);
  if (Array.isArray(values)) {
    return values.map((v) => v?.toLocaleString() ?? "").join(" - ");
  }

  return values?.toLocaleString() ?? "";
};

// Date

export const dateFilterOperatorCaptions: Record<DateObjectFilterOperator, string> = {
  Range: "is between",
  Empty: "is empty",
  NotEmpty: "is not empty",
};

export const getDateFilterValuesText = (filter: DateObjectFilter): string => {
  switch (filter.operator) {
    case "Range": {
      const from = convertISODateShort(filter.from) ?? "";
      const to = convertISODateShort(filter.to) ?? "";
      return `${from} - ${to}`;
    }
    case "Empty":
    case "NotEmpty":
      return "";
    default:
      return "";
  }
};

// MultiSelect

export const multiSelectFilterOperatorCaptions: Record<MultiSelectObjectFilterOperator, string> = {
  OneOf: "is one of",
  NotOneOf: "is not one of",
  Empty: "is empty",
  NotEmpty: "is not empty",
};

export const getMultiSelectFilterValuesText = (filter: MultiSelectObjectFilter, options?: SelectOption[]): string => {
  switch (filter.operator) {
    case "OneOf":
    case "NotOneOf": {
      const values = filter.selectedValues ?? [];
      if (options) {
        const optionMap = new Map(options.map((o) => [o.value, o.label]));
        return values.map((v) => optionMap.get(v) ?? v).join(", ");
      }
      return values.join(", ");
    }
    case "Empty":
    case "NotEmpty":
      return "";
    default:
      return "";
  }
};
